import { useState, useEffect } from 'react';
import JobStatusBadge from '../JobStatusBadge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { DollarSign, Check, Calendar } from 'lucide-react';
import useJobConfig from '../../hooks/useJobConfig';
import DateField from '../DateField';

export default function JobHeader({ job, currentStatus, canModify, onStatusChange, onDateChange, onChargeUpdate, onEndDateChange }) {
  const { visibleStatuses, labelForType } = useJobConfig();
  const [chargeInput, setChargeInput] = useState(job.charge_amount ?? '');
  const [saving, setSaving] = useState(false);
  const [endDateInput, setEndDateInput] = useState(job.end_date ? job.end_date.slice(0, 10) : '');

  // Keep the header in step when the job is updated elsewhere on the page
  // (e.g. the schedule slip banner moving the end date).
  useEffect(() => {
    setEndDateInput(job.end_date ? job.end_date.slice(0, 10) : '');
    setChargeInput(job.charge_amount ?? '');
  }, [job.end_date, job.charge_amount]);
  const [savingEndDate, setSavingEndDate] = useState(false);

  const handleChargeSave = async () => {
    const val = parseFloat(chargeInput);
    if (isNaN(val) || val < 0) return;
    setSaving(true);
    await onChargeUpdate(val);
    setSaving(false);
  };

  const handleEndDateSave = async () => {
    if (!endDateInput) return;
    setSavingEndDate(true);
    await onEndDateChange(endDateInput);
    setSavingEndDate(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="job-header">
      <div className="flex flex-col lg:flex-row lg:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-start gap-3 mb-2 min-w-0">
            <h3 className="text-xl sm:text-2xl font-bold text-slate-900 max-w-full [overflow-wrap:anywhere]" data-testid="job-number">Job #{job.job_number.split(/(?<=[_-])/).map((part, i) => <span key={i}>{part}<wbr /></span>)}</h3>
            <JobStatusBadge status={job.status} />
          </div>
          <p className="text-lg text-slate-600 mb-2 [overflow-wrap:anywhere]" data-testid="job-customer">{job.customer_name}</p>
          <p className="text-slate-700 mb-4 [overflow-wrap:anywhere]" data-testid="job-description">{job.description}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><span className="text-slate-500">Type:</span><span className="ml-2">{labelForType(job.job_type) || 'N/A'}</span></div>
            {!(job.equipment || []).length && (
              <>
                <div><span className="text-slate-500">Make/Model:</span><span className="ml-2">{job.make && job.model ? `${job.make} ${job.model}` : 'N/A'}</span></div>
                <div><span className="text-slate-500">Serial:</span><span className="ml-2 font-mono">{job.serial_number || 'N/A'}</span></div>
                <div><span className="text-slate-500">Asset ID:</span><span className="ml-2 font-mono">{job.unit_id || 'N/A'}</span></div>
              </>
            )}
            <div className="min-w-0 break-words" data-testid="job-site-location"><span className="text-slate-500">Location:</span><span className="ml-2">{job.customer_location || 'N/A'}</span></div>
            <div className="min-w-0 break-words" data-testid="job-site-lsd"><span className="text-slate-500">LSD:</span><span className="ml-2 font-mono">{job.customer_lsd || 'N/A'}</span></div>
            {job.site_details?.province && <div data-testid="job-site-province"><span className="text-slate-500">Province:</span><span className="ml-2">{job.site_details.province}</span></div>}
            {job.site_details?.address && <div className="min-w-0 break-words" data-testid="job-site-address"><span className="text-slate-500">Address:</span><span className="ml-2">{job.site_details.address}</span></div>}
          </div>
        </div>
        {canModify && (
          <div className="w-full lg:w-64 lg:shrink-0 space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500 mb-2 block">Status</Label>
              <Select value={job.status} onValueChange={onStatusChange}>
                <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {visibleStatuses.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {currentStatus?.hasDate && (
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500 mb-2 block">{currentStatus.dateLabel}</Label>
                <DateField value={job.status_dates?.[job.status] || ''} onChange={(v) => onDateChange(job.status, v)} data-testid="status-date-input" />
              </div>
            )}
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500 mb-2 block">End Date</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <DateField
                    value={endDateInput}
                    onChange={setEndDateInput}
                    data-testid="end-date-input"
                  />
                </div>
                <Button
                  size="sm"
                  onClick={handleEndDateSave}
                  disabled={savingEndDate}
                  className="bg-slate-900 px-3"
                  data-testid="save-end-date-btn"
                >
                  <Check size={14} />
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500 mb-2 block">Charge Amount</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <DollarSign size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={chargeInput}
                    onChange={(e) => setChargeInput(e.target.value)}
                    className="pl-7"
                    data-testid="charge-amount-input"
                  />
                </div>
                <Button
                  size="sm"
                  onClick={handleChargeSave}
                  disabled={saving}
                  className="bg-slate-900 px-3"
                  data-testid="save-charge-btn"
                >
                  <Check size={14} />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
